import React, { useState, useEffect, useCallback } from 'react';
import {
  FaFingerprint, FaSync, FaPlus, FaEdit, FaTrash, FaKey, FaCheckCircle,
  FaExclamationTriangle, FaServer, FaCopy,
} from 'react-icons/fa';
import api, { errorMessage } from '../api';
import {
  PageShell, Card, StatCard, Badge, Button, Modal, Field, inputClass, Alert,
  Loading, Table, formatDateTime,
} from './attendance/ui';

const BLANK = {
  name: '', serialNumber: '', location: '', deviceType: 'fingerprint',
  ipAddress: '', port: 4370, employees: 0, isActive: true,
};

/**
 * Urban biometric integration: register attendance machines, sync their
 * punches into attendance records and inspect the raw punch log.
 */
const BiometricIntegration = () => {
  const [devices, setDevices] = useState([]);
  const [logs, setLogs] = useState([]);
  const [locations, setLocations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [syncing, setSyncing] = useState(null);   // device id, or 'all'
  const [error, setError] = useState('');
  const [notice, setNotice] = useState('');
  const [showUnprocessed, setShowUnprocessed] = useState(false);

  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(BLANK);
  const [saving, setSaving] = useState(false);
  const [apiKey, setApiKey] = useState(null);

  const load = useCallback(async () => {
    setError('');
    try {
      const [deviceRes, logRes, locationRes] = await Promise.all([
        api.get('biometrics/machines/'),
        api.get('biometrics/logs/', {
          params: showUnprocessed ? { processed: false } : {},
        }),
        api.get('locations/'),
      ]);
      setDevices(deviceRes.data);
      setLogs(logRes.data.slice(0, 100));
      setLocations(locationRes.data);
    } catch (err) {
      setError(errorMessage(err, 'Could not load biometric data.'));
    } finally {
      setLoading(false);
    }
  }, [showUnprocessed]);

  useEffect(() => { load(); }, [load]);

  const syncAll = async () => {
    setSyncing('all');
    setNotice('');
    setError('');
    try {
      const { data } = await api.post('biometrics/machines/sync-all/');
      setNotice(`${data.processedLogs} punch(es) applied to attendance`
        + (data.unmatchedLogs ? `; ${data.unmatchedLogs} could not be matched to a staff member.` : '.'));
      await load();
    } catch (err) {
      setError(errorMessage(err, 'Sync failed.'));
    } finally {
      setSyncing(null);
    }
  };

  const syncOne = async (device) => {
    setSyncing(device.id);
    setError('');
    try {
      const { data } = await api.post(`biometrics/machines/${device.id}/sync/`);
      setNotice(`${device.name}: ${data.processedLogs} punch(es) applied.`);
      await load();
    } catch (err) {
      setError(errorMessage(err, 'Sync failed.'));
    } finally {
      setSyncing(null);
    }
  };

  const save = async (event) => {
    event.preventDefault();
    setSaving(true);
    setError('');
    const payload = Object.fromEntries(
      Object.entries(form).filter(([, value]) => value !== '' && value !== null),
    );
    try {
      if (editing === 'new') {
        await api.post('biometrics/machines/', payload);
        setNotice(`${form.name} registered.`);
      } else {
        await api.put(`biometrics/machines/${editing.id}/`, payload);
        setNotice(`${form.name} updated.`);
      }
      setEditing(null);
      await load();
    } catch (err) {
      setError(errorMessage(err, 'Could not save the device.'));
    } finally {
      setSaving(false);
    }
  };

  const remove = async (device) => {
    if (!window.confirm(`Remove ${device.name}?`)) return;
    try {
      await api.delete(`biometrics/machines/${device.id}/`);
      await load();
    } catch (err) {
      setError(errorMessage(err, 'Could not remove the device.'));
    }
  };

  const rotateKey = async (device) => {
    if (!window.confirm(`Rotate the push key for ${device.name}? The device must be reconfigured.`)) return;
    try {
      const { data } = await api.post(`biometrics/machines/${device.id}/rotate-key/`);
      setApiKey({ name: device.name, key: data.apiKey });
    } catch (err) {
      setError(errorMessage(err, 'Could not rotate the key.'));
    }
  };

  const online = devices.filter((device) => device.status === 'online').length;
  const pending = devices.reduce((total, device) => total + (device.pendingLogs || 0), 0);

  if (loading) return <PageShell title="Biometric Integration"><Loading /></PageShell>;

  return (
    <PageShell
      title="Biometric Integration"
      subtitle="Attendance machines at urban locations"
      actions={
        <>
          <Button variant="secondary" onClick={syncAll} disabled={syncing === 'all'}>
            <FaSync className={syncing === 'all' ? 'animate-spin' : ''} /> Sync all
          </Button>
          <Button onClick={() => { setForm(BLANK); setEditing('new'); }}>
            <FaPlus /> Add device
          </Button>
        </>
      }
    >
      <Alert>{error}</Alert>
      <Alert tone="success">{notice}</Alert>

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
        <StatCard icon={<FaServer />} label="Devices" tone="gray" value={devices.length} />
        <StatCard icon={<FaCheckCircle />} label="Online" tone="green" value={online} />
        <StatCard icon={<FaExclamationTriangle />} label="Offline" tone="red" value={devices.length - online} />
        <StatCard icon={<FaFingerprint />} label="Unprocessed punches" tone="amber" value={pending} />
      </div>

      <Card title="Registered devices" className="mb-6">
        <Table
          columns={['Device', 'Serial', 'Location', 'Type', 'Address', 'Enrolled',
            'Pending', 'Last sync', 'Status', 'Actions']}
          rows={devices}
          empty="No attendance machines registered yet."
          renderRow={(device) => (
            <tr key={device.id} className="border-b last:border-0 hover:bg-gray-50">
              <td className="py-3 pr-4 font-medium text-gray-800 whitespace-nowrap">{device.name}</td>
              <td className="py-3 pr-4 font-mono text-xs">{device.serialNumber || '—'}</td>
              <td className="py-3 pr-4 whitespace-nowrap">{device.location || '—'}</td>
              <td className="py-3 pr-4 capitalize">{device.deviceType}</td>
              <td className="py-3 pr-4 font-mono text-xs whitespace-nowrap">
                {device.ipAddress ? `${device.ipAddress}:${device.port}` : '—'}
              </td>
              <td className="py-3 pr-4">{device.employees}</td>
              <td className="py-3 pr-4">
                {device.pendingLogs > 0
                  ? <span className="font-semibold text-amber-600">{device.pendingLogs}</span>
                  : <span className="text-gray-400">0</span>}
              </td>
              <td className="py-3 pr-4 whitespace-nowrap text-xs">{formatDateTime(device.lastSync)}</td>
              <td className="py-3 pr-4"><Badge value={device.status} /></td>
              <td className="py-3 pr-4">
                <div className="flex items-center gap-1">
                  <button onClick={() => syncOne(device)} title="Sync now"
                    className="p-2 text-gray-400 hover:text-[#15A857] hover:bg-green-50 rounded-lg">
                    <FaSync className={syncing === device.id ? 'animate-spin' : ''} />
                  </button>
                  <button onClick={() => { setForm({ ...BLANK, ...device }); setEditing(device); }}
                    title="Edit" className="p-2 text-gray-400 hover:text-blue-600 hover:bg-blue-50 rounded-lg">
                    <FaEdit />
                  </button>
                  <button onClick={() => rotateKey(device)} title="Rotate push key"
                    className="p-2 text-gray-400 hover:text-amber-600 hover:bg-amber-50 rounded-lg">
                    <FaKey />
                  </button>
                  <button onClick={() => remove(device)} title="Remove"
                    className="p-2 text-gray-400 hover:text-red-500 hover:bg-red-50 rounded-lg">
                    <FaTrash />
                  </button>
                </div>
              </td>
            </tr>
          )}
        />
      </Card>

      <Card
        title="Punch log"
        action={
          <label className="flex items-center gap-2 text-sm text-gray-600 cursor-pointer">
            <input type="checkbox" checked={showUnprocessed}
              onChange={(event) => setShowUnprocessed(event.target.checked)}
              className="rounded accent-[#15A857]" />
            Unprocessed only
          </label>
        }
      >
        <Table
          columns={['Staff', 'Device code', 'Matched to', 'Timestamp', 'Type', 'Device', 'Method', 'State']}
          rows={logs}
          empty={showUnprocessed ? 'Every punch has been applied.' : 'No punches recorded yet.'}
          renderRow={(log) => (
            <tr key={log.id} className="border-b last:border-0 hover:bg-gray-50">
              <td className="py-3 pr-4 font-medium text-gray-800 whitespace-nowrap">{log.employeeName}</td>
              <td className="py-3 pr-4 font-mono text-xs">{log.employeeId}</td>
              <td className="py-3 pr-4 text-xs">
                {log.matchedEmployee || <span className="text-red-500 font-semibold">unmatched</span>}
              </td>
              <td className="py-3 pr-4 whitespace-nowrap text-xs">{formatDateTime(log.timestamp)}</td>
              <td className="py-3 pr-4 capitalize">{log.type}</td>
              <td className="py-3 pr-4 whitespace-nowrap">{log.machine || '—'}</td>
              <td className="py-3 pr-4 capitalize">{log.method}</td>
              <td className="py-3 pr-4">
                <Badge value={log.processed ? 'approved' : 'pending'}
                  label={log.processed ? 'Applied' : 'Pending'} />
              </td>
            </tr>
          )}
        />
      </Card>

      {/* Device form */}
      <Modal isOpen={!!editing} onClose={() => setEditing(null)}
        title={editing === 'new' ? 'Register attendance machine' : `Edit ${editing?.name || ''}`} size="max-w-2xl">
        <form onSubmit={save} className="space-y-4">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <Field label="Device name *">
              <input required value={form.name} className={inputClass}
                onChange={(event) => setForm({ ...form, name: event.target.value })} />
            </Field>
            <Field label="Serial number">
              <input value={form.serialNumber || ''} className={inputClass}
                onChange={(event) => setForm({ ...form, serialNumber: event.target.value })} />
            </Field>
            <Field label="Location">
              <select value={form.location || ''} className={inputClass}
                onChange={(event) => setForm({ ...form, location: event.target.value })}>
                <option value="">Unassigned</option>
                {locations.filter((item) => item.type === 'urban').map((item) => (
                  <option key={item.id} value={item.name}>{item.name}</option>
                ))}
              </select>
            </Field>
            <Field label="Device type">
              <select value={form.deviceType} className={inputClass}
                onChange={(event) => setForm({ ...form, deviceType: event.target.value })}>
                <option value="fingerprint">Fingerprint</option>
                <option value="face">Face recognition</option>
                <option value="rfid">RFID / card</option>
                <option value="hybrid">Hybrid</option>
              </select>
            </Field>
            <Field label="IP address">
              <input value={form.ipAddress || ''} placeholder="192.168.1.20" className={inputClass}
                onChange={(event) => setForm({ ...form, ipAddress: event.target.value })} />
            </Field>
            <Field label="Port">
              <input type="number" value={form.port} className={inputClass}
                onChange={(event) => setForm({ ...form, port: event.target.value })} />
            </Field>
            <Field label="Enrolled staff">
              <input type="number" min="0" value={form.employees} className={inputClass}
                onChange={(event) => setForm({ ...form, employees: event.target.value })} />
            </Field>
            <Field label="Active">
              <select value={String(form.isActive)} className={inputClass}
                onChange={(event) => setForm({ ...form, isActive: event.target.value === 'true' })}>
                <option value="true">Yes</option>
                <option value="false">No</option>
              </select>
            </Field>
          </div>
          <Alert tone="info">
            Devices push punches to <code>POST /api/biometrics/push/</code> with their
            key in the <code>X-Device-Key</code> header. Generate a key with the
            key button once the device is saved.
          </Alert>
          <div className="flex gap-3">
            <Button type="submit" disabled={saving} className="flex-1">
              {saving ? 'Saving…' : editing === 'new' ? 'Register device' : 'Save changes'}
            </Button>
            <Button type="button" variant="secondary" onClick={() => setEditing(null)}>Cancel</Button>
          </div>
        </form>
      </Modal>

      {/* Rotated key */}
      <Modal isOpen={!!apiKey} onClose={() => setApiKey(null)} title="Device push key" size="max-w-lg">
        {apiKey && (
          <div className="space-y-4">
            <Alert tone="warning">
              Copy this key into {apiKey.name} now — it is not shown again.
              The previous key stopped working immediately.
            </Alert>
            <div className="bg-gray-50 rounded-xl p-4 font-mono text-sm break-all flex items-start gap-3">
              <span className="flex-1">{apiKey.key}</span>
              <button onClick={() => navigator.clipboard?.writeText(apiKey.key)}
                className="p-2 text-gray-400 hover:text-[#15A857]" title="Copy">
                <FaCopy />
              </button>
            </div>
            <Button className="w-full" onClick={() => setApiKey(null)}>Done</Button>
          </div>
        )}
      </Modal>
    </PageShell>
  );
};

export default BiometricIntegration;
